/**
 * Where a brand kit can land in a project — found structurally, never guessed.
 *
 * Pure: the caller injects `parseHTML`, so this runs under jsdom, in the
 * browser, and in a Worker. It reports; it never mutates.
 *
 * THE PROBLEM THIS SOLVES. Applying a logo to a NEW project is easy — every
 * template stamps a sentinel and `replaceLogoSentinel` fills it. Applying one
 * to a project that already HAS a logo is the common case ("I built my site,
 * then made a logo") and has no prior art, because:
 *
 *   · `data-pb-needs-upload="logo"` is useless here — `replaceLogoSentinel`
 *     strips it the moment the sentinel resolves, so on the common case it is
 *     guaranteed absent.
 *   · `classifyImageRole` false-positives on things like
 *     `alt="Logos of our partners"`.
 *
 * The signal that works is the normalized `.logo-slot` structure, which is the
 * editor's OWN definition of a logo (`editing/logo-dom.js`). Using it means a
 * batch apply and a manual replace agree about what a logo is, by construction.
 */

import { iterateAllSections } from './iterate-sections.js';
import { LOGO_SENTINEL_VALUE } from './source-domain-url.js';
import { findLogoWrappers, isSourceLockedLogo, logoSlotImages } from '../editing/logo-dom.js';

/**
 * Every logo slot in the project.
 *
 * @param {object} state
 * @param {{parseHTML: (html: string) => Document}} deps
 * @returns {Array<{sectionPath, instanceId, kind, pageId, wrapperIndex, eid,
 *   hasSentinel, sourceLocked, variants, currentSrcs}>}
 */
export function findLogoTargets(state, { parseHTML } = {}) {
  if (typeof parseHTML !== 'function') return [];
  const targets = [];

  iterateAllSections(state, (entry) => {
    let doc;
    try {
      doc = parseHTML(`<div id="pb-logo-root">${entry.html}</div>`);
    } catch {
      return; // a section that will not parse is skipped, not guessed at
    }
    const root = doc?.getElementById?.('pb-logo-root');
    if (!root) return;

    findLogoWrappers(root).forEach((wrapper, wrapperIndex) => {
      const images = logoSlotImages(wrapper);
      targets.push({
        sectionPath: entry.sectionPath,
        instanceId: entry.instanceId,
        kind: entry.kind,
        pageId: entry.pageId ?? null,
        // Position within THIS section, so a target can be re-found on a later
        // re-import without depending on document order across the project.
        wrapperIndex,
        eid: wrapper.getAttribute('data-pb-eid') || null,
        hasSentinel: (entry.html || '').includes(LOGO_SENTINEL_VALUE),
        sourceLocked: isSourceLockedLogo(wrapper),
        variants: readVariants(images),
        currentSrcs: images.map((img) => img.getAttribute('src') || '')
      });
    });
  });

  return targets;
}

/**
 * Which light/dark slots this wrapper declares.
 *
 * A template that ships two marks is telling us it wants two; one that ships
 * one is telling us it wants one. This is READ, never invented — a brand apply
 * must not manufacture a dark variant a template never asked for, because it
 * has no way to know which backgrounds the second one would sit on.
 *
 * The `.light`/`.dark` class convention is undocumented and inferred from
 * `logo-editor.js`'s `readLogoInfo`; the tests are its documentation.
 */
export function readVariants(images) {
  const hasLight = images.some((img) => img.classList?.contains('light'));
  const hasDark = images.some((img) => img.classList?.contains('dark'));
  if (hasLight || hasDark) {
    return [...(hasLight ? ['light'] : []), ...(hasDark ? ['dark'] : [])];
  }
  return images.length > 1 ? ['light', 'dark'] : ['all'];
}

/** Does any surface still carry an unresolved logo sentinel? */
export function hasUnresolvedLogoSentinel(state) {
  let found = false;
  iterateAllSections(state, (entry) => {
    if (!found && (entry.html || '').includes(LOGO_SENTINEL_VALUE)) found = true;
  });
  return found;
}

/**
 * Pages whose SEO block has no social image yet.
 *
 * The default OG policy is fill-empty: an OG card is per-URL in the real world,
 * and a shared brand card on nine pages of ten beats nothing on nine of ten —
 * but a value the user chose is never clobbered, which is the same rule
 * `applyPalette` applies to hand-edited colours.
 */
export function findEmptyOgPages(state) {
  const pages = Array.isArray(state?.pages) ? state.pages : [];
  return pages
    .map((page, index) => ({ page, index }))
    .filter(({ page }) => !String(page?.seo?.ogImage || '').trim())
    .map(({ page, index }) => ({
      pageId: page.id ?? null,
      pageIndex: index,
      path: `pages.${index}.seo.ogImage`
    }));
}

/**
 * Summarise what a kit could change, for the confirm dialog.
 *
 * Counts are what a user can act on ("12 logos, 4 of them locked") — a list of
 * paths is not.
 */
export function summarizeTargets(targets) {
  const sourceLocked = targets.filter((t) => t.sourceLocked).length;
  return {
    total: targets.length,
    replaceable: targets.length - sourceLocked,
    sourceLocked,
    withSentinel: targets.filter((t) => t.hasSentinel).length,
    twoVariant: targets.filter((t) => t.variants.length === 2).length
  };
}
